/// <amd-module name="Repos/Views/Settings/ProjectBranchPolicies/Services/ProjectBranchPoliciesService" />
import { ITfsPageService } from "Tfs/Platform/Page";
import { IReadonlyObservableArray, IReadonlyObservableValue, ObservableArray, ObservableValue } from "VSS/Core/Observable";
import { localeIgnoreCaseComparer } from "VSS/Core/Util/String";
import { MakeOptional } from "VSS/Core/Util/Types";
import { IVssPageContext, Services, VssService } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";
import {
    IGetExistingBranchHintsRequestParameters,
    IGetExistingBranchHintsResult,
    IProjectBranchPoliciesService,
    IProjectPoliciesResult,
    IProjectPolicyItem
} from "../Contracts";

const ProjectPoliciesContributionId = "ms.vss-code-web.settings-project-branch-policies-data-provider";
const ExistingBranchesContributionId = "ms.vss-code-web.settings-project-branch-policies-branches-data-provider";

class ProjectBranchPoliciesService extends VssService implements IProjectBranchPoliciesService {
    private _hasEditPermission = new ObservableValue<boolean>(false);
    public readonly hasEditPermission: IReadonlyObservableValue<boolean> = this._hasEditPermission;

    private _repositoryCount = new ObservableValue<number>(0);
    public readonly repositoryCount: IReadonlyObservableValue<number> = this._repositoryCount;

    private _projectBranchPoliciesLoading = new ObservableValue<boolean>(true);
    public readonly projectBranchPoliciesLoading: IReadonlyObservableValue<boolean> = this._projectBranchPoliciesLoading;

    private _projectBranchPolicies = new ObservableArray<IProjectPolicyItem>();
    public readonly projectBranchPolicies: IReadonlyObservableArray<IProjectPolicyItem> = this._projectBranchPolicies;

    private _existingBranchHints = new ObservableValue<IGetExistingBranchHintsResult | undefined>(undefined);
    public readonly existingBranchHints: IReadonlyObservableValue<IGetExistingBranchHintsResult | undefined> = this._existingBranchHints;

    private contributionService: IVssContributionService;

    public _serviceStart(pageContext: IVssPageContext): void {
        super._serviceStart(pageContext);

        const tfsPageService = pageContext.getService<ITfsPageService>("ITfsPageService");

        this.contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");

        this.forceReloadProjectBranchPolicies(false);
    }

    public async reloadProjectBranchPoliciesAsync(): Promise<void> {
        if (!this._projectBranchPoliciesLoading.value) {
            await this.forceReloadProjectBranchPolicies(true);
        }
    }

    private async forceReloadProjectBranchPolicies(ignoreCache: boolean): Promise<void> {
        this._projectBranchPoliciesLoading.value = true;

        try {
            const result = await this.contributionService.getDataAsync<IProjectPoliciesResult>(ProjectPoliciesContributionId, {}, ignoreCache);

            const policies = (result && result.projectPolicies) || [];

            policies.sort(ProjectBranchPoliciesService.sortByScope);

            this._projectBranchPolicies.splice(0, this._projectBranchPolicies.length, ...policies);

            this._hasEditPermission.value = !!(result && !result.readonlyMode);
            this._repositoryCount.value = (result && result.repositoryCount) || 0;

            this._projectBranchPoliciesLoading.value = false;
        } catch (ex) {
            this._projectBranchPolicies.removeAll();
            this._repositoryCount.value = 0;
            this._projectBranchPoliciesLoading.value = false;
        }
    }

    public async getExistingBranchHintsAsync(branchPattern: string): Promise<void> {
        this._existingBranchHints.value = undefined;

        const requestParameters: IGetExistingBranchHintsRequestParameters = {
            branchPattern: branchPattern,
            top: 10
        };

        const result = await this.contributionService.getDataAsync<IGetExistingBranchHintsResult>(ExistingBranchesContributionId, requestParameters);

        this._existingBranchHints.value = { branchPattern, ...(result! as MakeOptional<IGetExistingBranchHintsResult, "branchPattern">) };
    }

    // Simple sort for presentation -- first "every branch" policies, then default-branch policies, then just alphabetically.
    // It would maybe be nice to put this in UI code rather than a service. On the other hand, I like the fact that the service
    // only makes these lists available as ReadOnly observable arrays, so the consumer can't really sort them.
    private static sortByScope = (a: IProjectPolicyItem, b: IProjectPolicyItem): number => {
        // This simplified logic works because scopes are unique -- i.e., you'll never have a === b

        if (a.scope === "*:refs/heads/*") {
            return -1;
        }
        if (b.scope === "*:refs/heads/*") {
            return +1;
        }

        if (a.scope === "*:~default") {
            return -1;
        }
        if (b.scope === "*:~default") {
            return +1;
        }

        return localeIgnoreCaseComparer(a.scope, b.scope);
    };
}

Services.add("IProjectBranchPoliciesService", { serviceFactory: ProjectBranchPoliciesService });
